import React, { useState } from 'react';
import { Music2, User, TrendingUp, TrendingDown, SkipForward } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const ScoringPanel = ({ gameId, gameState, onRefresh }) => {
  const [isScoring, setIsScoring] = useState(false);
  const currentTeam = gameState.teams[gameState.current_team_index];

  const addScore = async (eventType) => {
    setIsScoring(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/game/${gameId}/score`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          team_name: currentTeam.name,
          event_type: eventType,
        }),
      });

      if (response.ok) {
        const pointsMap = {
          title: '+4 points',
          artist: '+6 points',
          challenge_success: '+10 points',
          challenge_fail: '-10 points',
        };
        toast.success(`${currentTeam.name}: ${pointsMap[eventType]}`);
        await onRefresh();
      } else {
        toast.error('Failed to add score');
      }
    } catch (error) {
      console.error('Error adding score:', error);
      toast.error('Failed to add score');
    } finally {
      setIsScoring(false);
    }
  };

  const nextSong = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/game/${gameId}/next-song`, {
        method: 'POST',
      });

      if (response.ok) {
        toast.info('Next song!');
        await onRefresh();
      }
    } catch (error) {
      console.error('Error moving to next song:', error);
      toast.error('Failed to move to next song');
    }
  };

  const nextTeam = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/game/${gameId}/next-team`, {
        method: 'POST',
      });

      if (response.ok) {
        const data = await response.json();
        const newTeam = data.game.teams[data.game.current_team_index];
        toast.info(`${newTeam.name}'s turn!`);
        await onRefresh();
      }
    } catch (error) {
      console.error('Error moving to next team:', error);
      toast.error('Failed to move to next team');
    }
  };

  return (
    <Card data-testid="scoring-panel">
      <CardHeader className="bg-[hsl(var(--muted))] border-b">
        <CardTitle className="text-xl font-[var(--font-display)]">
          Quick Scoring
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6">
        <div className="space-y-4">
          {/* Main scoring buttons */}
          <div className="grid grid-cols-2 gap-3">
            <Button
              onClick={() => addScore('title')}
              disabled={isScoring}
              className="h-24 flex-col rounded-xl bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-white"
              data-testid="score-title-button"
            >
              <Music2 className="h-6 w-6 mb-2" />
              <span className="text-lg font-[var(--font-display)]">
                Song Title
              </span>
              <span className="text-sm opacity-90">
                +4 points
              </span>
            </Button>

            <Button
              onClick={() => addScore('artist')}
              disabled={isScoring}
              className="h-24 flex-col rounded-xl bg-[hsl(var(--secondary))] hover:bg-[hsl(var(--secondary))]/90 text-black"
              data-testid="score-artist-button"
            >
              <User className="h-6 w-6 mb-2" />
              <span className="text-lg font-[var(--font-display)]">
                Artist
              </span>
              <span className="text-sm opacity-90">
                +6 points
              </span>
            </Button>

            <Button
              onClick={() => addScore('challenge_success')}
              disabled={isScoring}
              variant="outline"
              className="h-24 flex-col rounded-xl border-2 border-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))] hover:text-white"
              data-testid="score-challenge-success-button"
            >
              <TrendingUp className="h-6 w-6 mb-2" />
              <span className="text-base font-[var(--font-display)]">
                Success Challenge
              </span>
              <span className="text-sm opacity-70">
                +10 points
              </span>
            </Button>

            <Button
              onClick={() => addScore('challenge_fail')}
              disabled={isScoring}
              variant="outline"
              className="h-24 flex-col rounded-xl border-2 border-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive))] hover:text-white"
              data-testid="score-challenge-fail-button"
            >
              <TrendingDown className="h-6 w-6 mb-2" />
              <span className="text-base font-[var(--font-display)]">
                Failed Challenge
              </span>
              <span className="text-sm opacity-70">
                -10 points
              </span>
            </Button>
          </div>

          {/* Navigation buttons */}
          <div className="pt-4 border-t space-y-2">
            <Button
              onClick={nextSong}
              variant="outline"
              className="w-full rounded-xl"
              data-testid="next-song-button"
            >
              <SkipForward className="mr-2 h-4 w-4" />
              Next Song ({gameState.current_song}/10)
            </Button>
            {gameState.current_song === 10 && (
              <Button
                onClick={nextTeam}
                variant="default"
                className="w-full rounded-xl"
                data-testid="next-team-button"
              >
                <SkipForward className="mr-2 h-4 w-4" />
                Next Team's Turn
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ScoringPanel;
